import { ApiClient } from './client';
import { AUTH_API_BASE_URL, DEFAULT_THREAD_ID } from '../utils/constants';
import type { ChatResponse, Message, FileAttachment } from '../types';

/** Элемент списка чатов с бэкенда */
interface RawChatItem {
  chat_id?: string;
  id?: string;
  text?: string;
  title?: string;
  created_at?: string;
  date?: string;
}

/** Ответ GET /api/v1/me/chats: { items: [{ chat_id, text }] } или { chats: [...] } */
interface MyChatsResponse {
  items?: RawChatItem[];
  chats?: RawChatItem[];
}

/** Элемент для сайдбара (id, title, date) */
export interface MyChatItem {
  id: string;
  title: string;
  date: string;
}

function formatChatDate(isoOrStr?: string): string {
  if (!isoOrStr) return '';
  const d = new Date(isoOrStr);
  if (Number.isNaN(d.getTime())) return isoOrStr;
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Сегодня';
  if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export class ChatApiService {
  private client: ApiClient;
  /** Клиент для сервиса чатов/авторизации, путь /api/v1/ */
  private authClient: ApiClient;

  constructor(client?: ApiClient) {
    this.client = client || new ApiClient();
    this.authClient = new ApiClient({ baseURL: `${AUTH_API_BASE_URL}/api/v1` });
  }

  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return this.client.healthCheck();
  }

  /** Список чатов пользователя (GET .../me/chats, с Authorization). Ответ: { items: [{ chat_id, text }] }. */
  async getMyChats(): Promise<MyChatItem[]> {
    const response = await this.authClient.get<MyChatsResponse | RawChatItem[]>('me/chats');
    const list = Array.isArray(response)
      ? response
      : (response as MyChatsResponse).items ?? (response as MyChatsResponse).chats ?? [];
    return list
      .map((item: RawChatItem) => ({
        id: item.chat_id ?? item.id ?? '',
        title: item.text ?? item.title ?? `Чат ${item.chat_id ?? item.id ?? ''}`,
        date: formatChatDate(item.created_at ?? item.date),
      }))
      .filter((item) => item.id.length > 0);
  }

  /** Сохранить chat id на бэкенде (POST .../me/chats?from=0&to=100 с телом { chat_id, text }). text — первые строчки чата или пустая строка. */
  async registerChat(chatId: string, text: string = ''): Promise<void> {
    if (!chatId?.trim()) return;
    await this.authClient.post<unknown>('me/chats?from=0&to=100', {
      chat_id: chatId,
      text: text ?? '',
    });
  }

  async sendMessage(
    message: string,
    threadId: string = DEFAULT_THREAD_ID,
    attachments?: FileAttachment[],
    signal?: AbortSignal,
    _isFirstMessage: boolean = false
  ): Promise<{ response: string; attachments?: FileAttachment[] }> {
    const hasText = message?.trim();
    const hasAttachments = attachments && attachments.length > 0;
    if (!hasText && !hasAttachments) {
      throw new Error('Message cannot be empty');
    }

    try {
      let apiResponse: ChatResponse;
      
      // Если есть файл, используем multipart/form-data
      if (attachments && attachments.length > 0) {
        const firstFile = attachments[0];
        
        if (firstFile.data && firstFile.data.trim()) {
          const base64Data = firstFile.data;
          const base64Match = base64Data.match(/^data:([^;]+);base64,(.+)$/);
          const mimeType = base64Match ? base64Match[1] : (firstFile.type || 'application/octet-stream');
          const base64String = base64Match ? base64Match[2] : base64Data;
          
          const byteCharacters = atob(base64String);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          const file = new File([blob], firstFile.name || 'file', { type: mimeType });
          
          const formData = new FormData();
          formData.append('message', message.trim());
          formData.append('thread_id', threadId);
          formData.append('file', file);
          
          apiResponse = await this.client.postFormData<ChatResponse>('/api/chat', formData, signal);
        } else {
          console.warn('Файл без данных:', { name: firstFile.name, type: firstFile.type });
          
          const requestBody: Record<string, unknown> = {
            message: message.trim(),
            thread_id: threadId
          };
          apiResponse = await this.client.postFormUrlEncoded<ChatResponse>('/api/chat', requestBody, signal);
        }
      } else {
        const requestBody: Record<string, unknown> = {
          message: message.trim(),
          thread_id: threadId
        };
        apiResponse = await this.client.postFormUrlEncoded<ChatResponse>('/api/chat', requestBody, signal);
      }
      
      if (apiResponse && typeof apiResponse === 'object' && 'response' in apiResponse) {
        return {
          response: String(apiResponse.response),
          attachments: (apiResponse as ChatResponse).attachments
        };
      }
      
      const responseText = typeof apiResponse === 'string' ? apiResponse : JSON.stringify(apiResponse);
      return {
        response: responseText,
        attachments: undefined
      };
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  }

  async getChatHistory(threadId: string): Promise<Message[]> {
    if (!threadId?.trim()) {
      console.warn('Thread ID is empty, returning empty history');
      return [];
    }

    try {
      const response = await this.client.get<{
        thread_id: string,
        messages: Array<{
          role: string,
          content: string | {response?: string, attachments?: FileAttachment[]},
          timestamp?: string
        }>
      }>('/api/history', { thread_id: threadId });
      
      if (response && response.messages && Array.isArray(response.messages)) {
        const messages: Message[] = response.messages.map((msg: {role: string, content: string | {response?: string, attachments?: FileAttachment[]}, timestamp?: string}, index: number) => {
          let contentString = '';
          let attachments: FileAttachment[] | undefined = undefined;

          let parsedContent: string | {response?: string, attachments?: FileAttachment[]} = msg.content;
          
          if (typeof msg.content === 'string') {
            try {
              const parsed = JSON.parse(msg.content) as {response?: string, attachments?: FileAttachment[]} | string;
              if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                parsedContent = parsed;
              } else {
                parsedContent = msg.content;
              }
            } catch {
              parsedContent = msg.content;
            }
          }

          if (typeof parsedContent === 'object' && parsedContent !== null && !Array.isArray(parsedContent)) {
            contentString = parsedContent.response || '';
            if (parsedContent.attachments && Array.isArray(parsedContent.attachments)) {
              attachments = parsedContent.attachments.map((att: FileAttachment) => ({
                ...att,
                id: String(att.id),
                uploadedAt: att.uploadedAt ? new Date(att.uploadedAt) : new Date(),
              }));
            }
          } else {
            contentString = typeof parsedContent === 'string' ? parsedContent : '';
          }

          return {
            id: `history-${threadId}-${index}`,
            type: (msg.role === 'user' ? 'human' : 'ai') as 'human' | 'ai',
            content: contentString,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            createdAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            updatedAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            attachments,
          };
        });
        
        return messages;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      return [];
    }
  }
}
